import { Table, TableColumn } from '@wix/design-system';
import React, { FC, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useStore } from 'react-redux';

import { logCategorySolutionListAction, logCategorySolutionListPresented } from '../../bi';
import { getArticlesByCategoryId } from '../../common/api/getArticlesByCategoryId';
import { CategoryItemType } from '../../common/api/getCategories';
import { SearchArticlesItem } from '../../common/api/searchArticles';
import { Loader } from '../../common/components/Loader';
import { ScrollBox } from '../../common/components/ScrollBox';
import { useBiEngagementEventHandlers } from '../../common/hooks/useBiEngagementEventHandlers';
import { useOnWidgetExpanded } from '../../common/hooks/useOnWidgetExpanded';
import { useHttpClient } from '../../common/providers/httpClient';
import { helpCenterSlice } from '../../common/reducers/helpCenter';
import { selectCategoryArticles } from '../../common/selectors/helpCenter';
import { experiments } from '../../experiments';
import { ArticlesListItem } from '../../help-center/components/ArticlesListItem';
import { CategoryPageArticleListItemWrapper } from '../../help-center/components/CategoryArticlesList';

const isSharedSearchResultsItem = experiments.is('specs.helpwidget.sharedSearchResultsItem', 'true');

const pageSize = 30;
let page = 1;

type Props = {
  category: CategoryItemType;
};

export const dataHook = 'category-articles';

export const CategoryArticles: FC<Props> = ({ category }) => {
  const httpClient = useHttpClient();
  const store = useStore();
  const { id: categoryId } = category;
  const containerRef = useRef<HTMLDivElement>(null);
  const [container, setContainer] = useState<RefObject<HTMLDivElement> | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const articles = useSelector(selectCategoryArticles);
  const { onScroll } = useBiEngagementEventHandlers();

  const columns: TableColumn<SearchArticlesItem>[] = React.useMemo(
    () => [
      {
        title: '', // because of titleBarVisible={false}
        style: { padding: 0, boxShadow: 'none' },
        render: (article, i) =>
          isSharedSearchResultsItem ? (
            <CategoryPageArticleListItemWrapper article={article} rowNumber={i} />
          ) : (
            <ArticlesListItem
              id={article.id}
              interactionLogger={logCategorySolutionListAction}
              rowNumber={i}
              title={article.title}
              categoryId={categoryId}
            />
          ),
      },
    ],
    [categoryId],
  );

  const fetchArticles = useCallback(async () => {
    const newArticles = await getArticlesByCategoryId(categoryId, pageSize, page++, httpClient);

    if (newArticles.length < pageSize) {
      setHasMore(false);
    }

    if (!category.children?.length) {
      logCategorySolutionListPresented(store.getState(), newArticles);
    }
    store.dispatch(helpCenterSlice.actions.addCategoryArticles(newArticles));
  }, [store, articles, category.children?.length, categoryId, httpClient]);

  const resetArticles = useCallback(() => {
    page = 1;
    setHasMore(true);
    store.dispatch(helpCenterSlice.actions.setCategoryArticles([]));
  }, []);

  useEffect(() => {
    setContainer(containerRef);
  }, []);

  useOnWidgetExpanded((expanded: boolean) => {
    if (expanded && articles) {
      logCategorySolutionListPresented(store.getState(), articles);
    }
  });

  useEffect(() => {
    resetArticles();
    void fetchArticles();
  }, [categoryId]);

  return (
    <ScrollBox ref={containerRef} onScroll={onScroll} width="100%" height="100%" dataHook={dataHook}>
      {articles.length ? (
        <Table
          infiniteScroll
          hasMore={hasMore}
          loadMore={fetchArticles}
          itemsPerPage={pageSize}
          data={articles}
          columns={columns}
          scrollElement={container! && container.current!}
          loader={<Loader dataHook="category-articles__loader" margin="25px" />}
        >
          <Table.Content titleBarVisible={false} />
        </Table>
      ) : (
        <Loader dataHook="category-articles__loader" height="100%" verticalAlign="middle" />
      )}
    </ScrollBox>
  );
};
